import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { ErrorMessage, Field, Formik } from 'formik';
import * as Bixby from '$/core';
import BixButton from '$/components/global/bixButton';
import BixCheckbox from '$/components/global/bixCheckbox';
import InputBlock from '$/components/global/inputBlock';

interface IAddSponsorProps {
  isUpdate: boolean;
  updateValues?: Bixby.Models.PrivateEquitySponsor;
  onSubmit: (values: AddPrivateEquitySponsorValues) => Promise<void>;
  onUpdate: (values: Bixby.Models.PrivateEquitySponsor) => Promise<void>;
}

export interface AddPrivateEquitySponsorValues {
  url?: string;
  name: string;
  ticker?: string;
  isPublic?: boolean;
}

const AddSponsor: React.FC<IAddSponsorProps> = props => {
  const { isUpdate, onSubmit, onUpdate, updateValues } = props;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [initialSponsorValues, setInitialSponsorValues] = useState(
    isUpdate && updateValues
      ? {
          isPublic: updateValues.isPublic,
          name: updateValues.name,
          ticker: updateValues.ticker,
          url: updateValues.url,
        }
      : {
          isPublic: false,
          name: '',
          ticker: '',
          url: '',
        }
  );

  useEffect(() => {
    setInitialSponsorValues(
      isUpdate && updateValues
        ? {
            isPublic: updateValues.isPublic || false,
            name: updateValues.name,
            ticker: updateValues.ticker || '',
            url: updateValues.url || '',
          }
        : {
            isPublic: false,
            name: '',
            ticker: '',
            url: '',
          }
    );
  }, [isUpdate, updateValues]);

  const schema = Yup.object({
    name: Yup.string().trim().max(256, "Name can't be more than 256 characters").required('Name is required.'),
    url: Yup.string()
      .trim()
      .matches(
        //eslint-disable-next-line
        /^((https?):\/\/)?([a-zA-Z0-9-]+\.[a-zA-Z0-9-.]+)(:\d+)?(\/[^\s\/][^\s]*)?(\?[^\s]*)?(#[^\s]*)?$/,
        'Invalid URL'
      )
      .max(256, "URL can't be more than 256 characters"),
    ticker: Yup.string().trim().max(50, "Ticker can't be more than 50 characters"),
  });
  return (
    <section className="new-private-equity-sponsor-form">
      <Formik
        initialValues={initialSponsorValues}
        validationSchema={schema}
        enableReinitialize={true}
        onSubmit={async (values: AddPrivateEquitySponsorValues, { resetForm }) => {
          setIsSubmitting(true);
          isUpdate && updateValues ? await onUpdate({ ...values, id: updateValues?.id }) : await onSubmit(values);
          setIsSubmitting(false);
          resetForm();
        }}
        render={({ handleSubmit, values, setFieldValue }) => (
          <form onSubmit={handleSubmit}>
            <InputBlock title="Name" required>
              <Field type="text" name="name" />
            </InputBlock>
            <ErrorMessage name="name">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>

            <InputBlock title="URL">
              <Field type="text" name="url" />
            </InputBlock>
            <ErrorMessage name="url">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>

            <InputBlock title="Ticker">
              <Field type="text" name="ticker" />
            </InputBlock>
            <ErrorMessage name="ticker">{msg => <div className="error error-message">{msg}</div>}</ErrorMessage>
            <InputBlock>
              <BixCheckbox
                id="isPublic"
                checked={values.isPublic}
                title="Is Public"
                onChange={() => setFieldValue('isPublic', !values.isPublic)}>
                Is Public
              </BixCheckbox>
            </InputBlock>
            {isSubmitting && <div>Loading...</div>}
            <BixButton type="submit" disabled={isSubmitting} onClick={handleSubmit}>
              {isUpdate ? 'Update' : 'Add'}
            </BixButton>
          </form>
        )}></Formik>
    </section>
  );
};

export default AddSponsor;
